import TimelineOutlinedIcon from "@mui/icons-material/TimelineOutlined";
import { Box } from "@mui/material";
import type { FC } from "react";
import MetaChip from "@/ui/Badges/MetaChip";
import { FactList } from "@/ui/molecules/FactList";
import { ExplainerCard, type ExplainerCardWidth } from "@/ui/Panels/ExplainerCard";
import type { RequestLedger, ShiftRequest } from "@/utils/models";
import { leadingEntry, ledgerTally } from "@/utils/requestOriginsLedger";
import { RequestOriginFooter } from "./RequestOriginFooter";
import { RequestOriginHppdMeter } from "./RequestOriginHppdMeter";
import { RequestOriginLedgerList } from "./RequestOriginLedgerList";
import { batchLabel, cardTitle, eyebrow } from "./requestOriginCopy";
import { entryFacts, hppdNumbersOf } from "./requestOriginFacts";
import { entryTileLook, ORIGIN_KIND_ICON, type RequestOriginViewLevel } from "./requestOriginMeta";
import { sentence } from "./requestOriginSentence";
import { useOriginCopyContext } from "./useOriginCopyContext";
import { useTrackOriginViewed } from "./useTrackOriginViewed";

type Props = {
	request: ShiftRequest;
	ledger: RequestLedger;
	level: RequestOriginViewLevel;
	width: ExplainerCardWidth;
};

/**
 * "Why this shift was posted". One entry: its sentence (with the numbers behind an HPPD post), and the facts. Several:
 * every change to the request's open slots as one story — what opened them, what closed them — and where it stands now.
 * The same card in the hover card and the phone sheet.
 */
export const RequestOriginCard: FC<Props> = ({ request, ledger, level, width }) => {
	const copy = useOriginCopyContext();
	useTrackOriginViewed(request, ledger, level);
	const tally = ledgerTally(ledger);
	const leading = leadingEntry(ledger);
	const testId = `request-origin-card-${request.id}`;
	const header = { dataTestId: testId, width, eyebrow: eyebrow(copy), title: cardTitle(ledger, copy), ...entryTileLook(leading) };

	if (ledger.entries.length > 1) {
		return (
			<ExplainerCard
				{...header}
				icon={<TimelineOutlinedIcon />}
				footer={<RequestOriginFooter tally={tally} copy={copy} dataTestId={`request-origin-footer-${request.id}`} />}
			>
				<RequestOriginLedgerList
					entries={ledger.entries}
					variant="timeline"
					copy={copy}
					dataTestId={`request-origin-flow-${request.id}`}
					entryTestIdPrefix={`request-origin-entry-${request.id}`}
				/>
			</ExplainerCard>
		);
	}

	const Icon = ORIGIN_KIND_ICON[leading.kind];
	const hppd = hppdNumbersOf(leading);
	const batch = batchLabel(leading, copy);
	const facts = entryFacts(leading, copy);
	return (
		<ExplainerCard {...header} icon={<Icon />} facts={facts.length > 0 ? <FactList items={facts} /> : undefined}>
			<Box component="p" data-testid={`request-origin-entry-${request.id}-0`} sx={{ m: 0 }}>
				{sentence(leading, false, copy)}
			</Box>
			{hppd !== null && (
				<Box sx={{ mt: 1.25 }}>
					<RequestOriginHppdMeter numbers={hppd} copy={copy} dataTestId={`${testId}-meter`} />
				</Box>
			)}
			{batch !== null && (
				<Box sx={{ mt: 1 }}>
					<MetaChip size="small" tone="neutral" label={batch} dataTestId={`${testId}-batch`} />
				</Box>
			)}
		</ExplainerCard>
	);
};
